import type { ConnectorId, ConnectorSnapshot } from "./connectors/types";
import {
  signalObservationTimelineEvents,
  type SignalSourceObservation,
} from "./signal-source-registry";
import type { SystemRecord } from "./systems";
import type { TimelineDomain, TimelineEvidenceEvent } from "./timeline-types";

export type TimelineLens = "unified" | TimelineDomain;

export type TimelineSequences = Record<TimelineLens, TimelineEvidenceEvent[]>;

function eventTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareEvents(left: TimelineEvidenceEvent, right: TimelineEvidenceEvent) {
  const timeDifference = eventTime(right.at) - eventTime(left.at);
  if (timeDifference) return timeDifference;
  return left.title.localeCompare(right.title);
}

function label(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function unique<T>(values: T[]) {
  return Array.from(new Set(values));
}

function currentMarketObservation(connector: ConnectorSnapshot): SignalSourceObservation {
  return {
    id: `current:${connector.id}`,
    sourceId: connector.id,
    kind: "market_snapshot",
    observedAt: connector.observedAt,
    title: connector.headline,
    detail: `${connector.instrument} · ${connector.primary.label} ${connector.primary.value} · ${connector.secondary.label} ${connector.secondary.value}`,
    url: connector.docsUrl,
    systemIds: [],
  };
}

function currentMediaObservations(connector: ConnectorSnapshot): SignalSourceObservation[] {
  if (connector.category !== "media" || !connector.media) return [];
  return connector.media.items.map((item) => ({
    id: `current:${connector.id}:${item.videoId ?? `${item.title}:${item.publishedAt}`}`,
    sourceId: connector.id,
    kind: "media_item",
    observedAt: item.publishedAt,
    title: item.title,
    detail: item.type,
    url: item.url,
    systemIds: [],
    externalId: item.videoId,
    mediaType: item.type,
    durationSeconds: item.durationSeconds,
  }));
}

function marketHourKey(observation: SignalSourceObservation) {
  const parsed = new Date(observation.observedAt);
  const hour = Number.isNaN(parsed.getTime())
    ? observation.observedAt
    : new Date(parsed.setUTCMinutes(0, 0, 0)).toISOString();
  return `${observation.sourceId}:${hour}`;
}

function marketTimelineEvents(
  connectors: ConnectorSnapshot[],
  observations: SignalSourceObservation[],
) {
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));
  const hourly = new Map<string, SignalSourceObservation>();

  observations
    .filter((observation) => observation.kind === "market_snapshot")
    .forEach((observation) => {
      const existing = hourly.get(marketHourKey(observation));
      if (!existing || eventTime(observation.observedAt) >= eventTime(existing.observedAt)) {
        hourly.set(marketHourKey(observation), observation);
      }
    });

  connectors
    .filter((connector) => connector.category === "market" && connector.status === "live")
    .map(currentMarketObservation)
    .forEach((observation) => {
      const existing = hourly.get(marketHourKey(observation));
      if (!existing || eventTime(observation.observedAt) >= eventTime(existing.observedAt)) {
        hourly.set(marketHourKey(observation), observation);
      }
    });

  return Array.from(hourly.values()).flatMap<TimelineEvidenceEvent>((observation) => {
    const connector = connectorById.get(observation.sourceId);
    if (!connector || connector.category !== "market") return [];
    const detail = observation.detail.includes(connector.instrument)
      ? observation.detail
      : `${connector.instrument} · ${observation.detail}`;
    return [{
      id: `market:${observation.sourceId}:${observation.observedAt}`,
      at: observation.observedAt,
      kind: label(connector.kind),
      sourceIds: [observation.sourceId],
      title: observation.title === connector.instrument ? connector.headline : observation.title,
      detail,
      url: observation.url ?? connector.docsUrl,
      domain: "markets",
      subtype: connector.kind,
      evidenceState: "observed",
      systemIds: observation.systemIds,
    }];
  }).sort(compareEvents);
}

function mediaTimelineEvents(
  connectors: ConnectorSnapshot[],
  observations: SignalSourceObservation[],
) {
  const allObservations = [
    ...observations,
    ...connectors.flatMap(currentMediaObservations),
  ];
  return signalObservationTimelineEvents(allObservations).map<TimelineEvidenceEvent>((event) => ({
    ...event,
    domain: "media",
    subtype: event.kind,
    evidenceState: "observed",
  })).sort(compareEvents);
}

function systemTimelineEvents(
  connectors: ConnectorSnapshot[],
  systemRecords: SystemRecord[],
) {
  const connectorById = new Map(connectors.map((connector) => [connector.id, connector]));

  return systemRecords.flatMap<TimelineEvidenceEvent>((system) => {
    const linkedInstitutions = system.sourceLinks
      .map((source) => connectorById.get(source.id))
      .filter((connector): connector is ConnectorSnapshot =>
        connector?.category === "institution");
    if (!linkedInstitutions.length) return [];

    const officialSources = linkedInstitutions.flatMap((connector) =>
      connector.institution?.officialSources ?? []);
    const surfaceCounts = officialSources.reduce<Map<string, number>>((counts, source) => {
      counts.set(source.type, (counts.get(source.type) ?? 0) + 1);
      return counts;
    }, new Map());
    const filingSurfaceCount = officialSources.filter((source) =>
      source.type === "Filings" || source.type === "Annual reports"
      || source.type === "Shareholder letters").length;
    const governmentSources = linkedInstitutions.filter((connector) =>
      connector.kind === "government / regulator"
      || connector.kind === "public data provider");
    const latest = linkedInstitutions.reduce((current, connector) =>
      eventTime(connector.observedAt) > eventTime(current)
        ? connector.observedAt
        : current,
    linkedInstitutions[0].observedAt);
    const surfaceSignals = Array.from(surfaceCounts.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([surface, count]) => `${surface} · ${count} registered`);

    return [{
      id: `system-register:${system.id}`,
      at: latest,
      kind: "System mechanism register",
      sourceIds: unique(linkedInstitutions.map((connector) => connector.id as ConnectorId)),
      title: system.name,
      detail: `${system.mechanisms.length} modeled mechanisms · ${officialSources.length} official evidence surfaces · ${filingSurfaceCount} filing or report surfaces · ${governmentSources.length} government or public-data sources.`,
      domain: "systems",
      subtype: system.type,
      evidenceState: "registered",
      systemIds: [system.id],
      signals: [
        ...system.mechanisms.map((mechanism) => `Mechanism · ${mechanism.name}`),
        ...surfaceSignals,
      ],
    }];
  }).sort(compareEvents);
}

export function buildTimelineSequences(
  connectors: ConnectorSnapshot[],
  observations: SignalSourceObservation[],
  systemRecords: SystemRecord[],
): TimelineSequences {
  const markets = marketTimelineEvents(connectors, observations);
  const media = mediaTimelineEvents(connectors, observations);
  const systems = systemTimelineEvents(connectors, systemRecords);
  return {
    markets,
    systems,
    media,
    unified: [...markets, ...systems, ...media].sort(compareEvents),
  };
}

export function timelineSubtypeCounts(events: TimelineEvidenceEvent[]) {
  const counts = events.reduce<Map<string, number>>((summary, event) => {
    const subtype = event.subtype ?? event.kind;
    summary.set(subtype, (summary.get(subtype) ?? 0) + 1);
    return summary;
  }, new Map());
  return Array.from(counts.entries())
    .map(([subtype, count]) => ({ subtype, count }))
    .sort((left, right) => right.count - left.count || left.subtype.localeCompare(right.subtype));
}
